import React from 'react';
import { summarizeLinks } from '../utils/ispLinkSummary';
import { useTranslation } from 'react-i18next';
import { Wifi, ExternalLink } from 'lucide-react';
import CompanyLogo from './CompanyLogo';

// Shared "Enlaces de Internet" widget — the same per-site / per-link view used
// in the company detail page, reused on the agent home dashboard and the client
// portal dashboard. Redundancy is per-location (dual-WAN on the same gateway);
// "UP" mirrors the monitor worker rule: only a confirmed-critical link counts
// as down (warning/unknown ≠ down).
//
// Props:
//   links          array of { id, isp_provider, name, isp_plan, isp_public_ip,
//                             status, location_name, company_name, company_logo }
//   onLinkClick    (id) => void   — click a single link
//   onViewAll      () => void     — footer "Ver en Monitor" link (optional)
//   groupByCompany bool           — also group by company (agent dashboard,
//                                    where admins see several companies)
const dotColor = (s) => s === 'healthy' ? '#22c55e' : s === 'critical' ? '#ef4444' : s === 'warning' ? '#f59e0b' : '#9ca3af';

function LocationGroup({ locName, links, onLinkClick, t }) {
  const { color, i18nKey, i18nParams } = summarizeLinks(links);
  const summary = t(i18nKey, i18nParams);
  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontWeight: 700, color, fontSize: 12 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
        {locName && <span style={{ color: 'var(--c-text)' }}>{locName}:</span>}
        <span>{summary}</span>
      </div>
      {links.map(link => (
        <div
          key={link.id}
          onClick={onLinkClick ? () => onLinkClick(link.id) : undefined}
          title={onLinkClick ? t('companies.viewInMonitor') : undefined}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0 3px 14px', cursor: onLinkClick ? 'pointer' : 'default', borderRadius: 4 }}
          onMouseEnter={e => { if (onLinkClick) e.currentTarget.style.background = 'var(--c-surface-2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor(link.status), flexShrink: 0 }} />
          <span style={{ fontWeight: 600 }}>{link.isp_provider || link.name}</span>
          <span style={{ color: 'var(--c-text-muted)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[link.isp_plan, link.isp_public_ip].filter(Boolean).join(' · ')}
          </span>
          <span style={{
            fontSize: 9, padding: '1px 6px', borderRadius: 3, fontWeight: 600, marginLeft: 'auto', flexShrink: 0,
            background: `${dotColor(link.status)}1f`, color: dotColor(link.status),
          }}>
            {link.status === 'healthy' ? 'UP' : link.status === 'critical' ? 'DOWN' : link.status === 'warning' ? 'WARN' : '?'}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function IspLinksWidget({ links = [], onLinkClick, onViewAll, groupByCompany = false, onCompanyClick }) {
  const { t } = useTranslation();
  if (!links.length) return null;

  // Group by location (single company) or company → location (agent dashboard).
  const byLocation = (list) => {
    const m = {};
    list.forEach(l => { const k = l.location_name || ''; (m[k] = m[k] || []).push(l); });
    return Object.entries(m);
  };

  let body;
  if (groupByCompany) {
    const byCompany = {};
    links.forEach(l => { const k = l.company_name || ''; (byCompany[k] = byCompany[k] || []).push(l); });
    body = Object.entries(byCompany).map(([companyName, cLinks]) => (
      <div key={companyName} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {companyName && (() => {
          const cid = cLinks[0]?.company_id;
          const clickable = onCompanyClick && cid;
          return (
            <div
              onClick={clickable ? () => onCompanyClick(cid) : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
                color: clickable ? 'var(--c-brand)' : 'var(--c-text-muted)',
                cursor: clickable ? 'pointer' : 'default',
              }}
            >
              <CompanyLogo name={companyName} logoUrl={cLinks[0]?.company_logo} size={16} /> {companyName}
            </div>
          );
        })()}
        {byLocation(cLinks).map(([locName, lLinks]) => (
          <LocationGroup key={locName} locName={locName} links={lLinks} onLinkClick={onLinkClick} t={t} />
        ))}
      </div>
    ));
  } else {
    body = byLocation(links).map(([locName, lLinks]) => (
      <LocationGroup key={locName} locName={locName} links={lLinks} onLinkClick={onLinkClick} t={t} />
    ));
  }

  return (
    <div className="card" style={{ margin: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
        <Wifi size={15} style={{ color: 'var(--c-brand)' }} /> {t('companies.internetLinks')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {body}
        {onViewAll && (
          <div
            onClick={onViewAll}
            style={{ fontSize: 11, color: '#3b82f6', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <ExternalLink size={10} /> {t('companies.viewInMonitor')}
          </div>
        )}
      </div>
    </div>
  );
}
